/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useEffect, useState } from 'react';

import { useResolvedTheme } from '@trebox-foundation/foundation-sdk';

import {
  listOIDCProviders,
  useLoginService,
  type OIDCProvider,
} from './service';
import { FormPanel } from './form-panel';
import { BrandPanel } from './brand-panel';
import './tokens.css';

// Dev/E2E autofill: skip manual credential entry when build is not
// `production`. Default matches conf/admin/bootstrap.yaml so a fresh
// treboxd + rsbuild dev session can log in with one click.
const isDevBuild = process.env.NODE_ENV !== 'production';
const DEV_DEFAULT_IDENTIFIER = isDevBuild ? 'admin' : '';
const DEV_DEFAULT_PASSWORD = isDevBuild ? 'admin123' : '';

// Toggle a body class so tokens.css can neutralise the global
// `min-width: 1200px` clamp inherited from global.less. The login page is the
// only surface that must follow the actual browser-window width.
const useBodyClass = (className: string) => {
  useEffect(() => {
    document.body.classList.add(className);
    document.documentElement.classList.add(className);
    return () => {
      document.body.classList.remove(className);
      document.documentElement.classList.remove(className);
    };
  }, [className]);
};

export const LoginPage: FC = () => {
  useBodyClass('trebox-login-active');
  const theme = useResolvedTheme();

  const [email, setEmail] = useState(DEV_DEFAULT_IDENTIFIER);
  const [password, setPassword] = useState(DEV_DEFAULT_PASSWORD);
  const [providers, setProviders] = useState<OIDCProvider[]>([]);

  const { login, register, loginLoading, registerLoading } = useLoginService({
    email,
    password,
  });

  useEffect(() => {
    let cancelled = false;
    listOIDCProviders().then(list => {
      if (!cancelled) {
        setProviders(list);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="trebox-login-root" data-theme={theme}>
      <BrandPanel />
      <FormPanel
        email={email}
        password={password}
        loginLoading={loginLoading}
        registerLoading={registerLoading}
        providers={providers}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onLogin={login}
        onRegister={register}
      />
    </div>
  );
};
